import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useSysGetConfig,
  useSysSetConfig,
} from "@tubo/api-models/hooks/system-config-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { Switch } from "@tubo/ui/components/ui/switch";
import { toast } from "sonner";
import {
  FALLBACK_LOGIN_DEFAULT_ROUTE,
  LOGIN_DEFAULT_ROUTE_CONFIG_KEY,
  normalizeLoginDefaultRoute,
  resolveLoginDefaultRoute,
} from "@client/lib/login-default-route";

const FRONTEND_URL_KEY = "frontend.url";
const ALLOW_SELF_REGISTRATION_KEY = "auth.allowSelfRegistration";
const ICP_BEIAN_NO_KEY = "site.icpBeianNo";
const POLICE_BEIAN_NO_KEY = "site.policeBeianNo";
const AGENT_TOOL_PYTHON_BIN_KEY = "agent-tool.pythonBin";

function SystemConfigPage() {
  const { data: frontendUrlData, isLoading: isFrontendUrlLoading } =
    useSysGetConfig(FRONTEND_URL_KEY);
  const { data: loginDefaultRouteData, isLoading: isLoginDefaultRouteLoading } =
    useSysGetConfig(LOGIN_DEFAULT_ROUTE_CONFIG_KEY);
  const { data: allowRegistrationData, isLoading: isAllowRegistrationLoading } =
    useSysGetConfig(ALLOW_SELF_REGISTRATION_KEY);
  const { data: icpBeianData, isLoading: isIcpBeianLoading } =
    useSysGetConfig(ICP_BEIAN_NO_KEY);
  const { data: policeBeianData, isLoading: isPoliceBeianLoading } =
    useSysGetConfig(POLICE_BEIAN_NO_KEY);
  const setFrontendUrlMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success("已保存");
      },
      onError: () => {
        toast.error("保存失败");
      },
    },
  });
  const setLoginDefaultRouteMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success("已保存");
      },
      onError: () => {
        toast.error("保存失败");
      },
    },
  });
  const setAllowRegistrationMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success("已保存");
      },
      onError: () => {
        toast.error("保存失败");
      },
    },
  });
  const setBeianMutation = useSysSetConfig();

  const [frontendUrl, setFrontendUrl] = useState("");
  const [loginDefaultRoute, setLoginDefaultRoute] = useState(
    FALLBACK_LOGIN_DEFAULT_ROUTE,
  );
  // 未配置时默认允许注册（与后端 defaultValue 一致）
  const [allowRegistration, setAllowRegistration] = useState(true);
  const [icpBeianNo, setIcpBeianNo] = useState("");
  const [policeBeianNo, setPoliceBeianNo] = useState("");

  useEffect(() => {
    if (frontendUrlData) {
      setFrontendUrl(String(frontendUrlData.value ?? ""));
    }
  }, [frontendUrlData]);

  useEffect(() => {
    if (loginDefaultRouteData) {
      setLoginDefaultRoute(
        resolveLoginDefaultRoute(loginDefaultRouteData.value),
      );
    }
  }, [loginDefaultRouteData]);

  useEffect(() => {
    if (allowRegistrationData) {
      setAllowRegistration(allowRegistrationData.value !== "false");
    }
  }, [allowRegistrationData]);

  useEffect(() => {
    if (icpBeianData) {
      setIcpBeianNo(String(icpBeianData.value ?? ""));
    }
  }, [icpBeianData]);

  useEffect(() => {
    if (policeBeianData) {
      setPoliceBeianNo(String(policeBeianData.value ?? ""));
    }
  }, [policeBeianData]);

  const handleFrontendUrlSave = () => {
    setFrontendUrlMutation.mutate({
      key: FRONTEND_URL_KEY,
      data: { value: frontendUrl },
    });
  };

  const handleLoginDefaultRouteSave = () => {
    const normalizedRoute = normalizeLoginDefaultRoute(loginDefaultRoute);
    if (!normalizedRoute) {
      toast.error("请输入有效的站内路由，且不能使用首页或登录注册页面");
      return;
    }

    setLoginDefaultRoute(normalizedRoute);
    setLoginDefaultRouteMutation.mutate({
      key: LOGIN_DEFAULT_ROUTE_CONFIG_KEY,
      data: { value: normalizedRoute },
    });
  };

  const handleAllowRegistrationChange = (checked: boolean) => {
    // 始终同步开关显示值，仅对保存动作做乐观更新；失败时由 invalidate 回读真实值
    setAllowRegistration(checked);
    setAllowRegistrationMutation.mutate({
      key: ALLOW_SELF_REGISTRATION_KEY,
      data: { value: checked ? "true" : "false" },
    });
  };

  const handleBeianSave = async () => {
    try {
      await Promise.all([
        setBeianMutation.mutateAsync({
          key: ICP_BEIAN_NO_KEY,
          data: { value: icpBeianNo.trim() },
        }),
        setBeianMutation.mutateAsync({
          key: POLICE_BEIAN_NO_KEY,
          data: { value: policeBeianNo.trim() },
        }),
      ]);
      toast.success("已保存");
    } catch {
      toast.error("保存失败");
    }
  };

  if (
    isFrontendUrlLoading ||
    isLoginDefaultRouteLoading ||
    isAllowRegistrationLoading ||
    isIcpBeianLoading ||
    isPoliceBeianLoading
  ) {
    return (
      <div className="py-8 text-center text-muted-foreground">加载中...</div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col gap-6">
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">系统配置</h2>
          <div className="space-y-2">
            <Label>前端访问地址</Label>
            <Input
              value={frontendUrl}
              onChange={(e) => setFrontendUrl(e.target.value)}
              placeholder="https://example.com"
            />
            <p className="text-xs text-muted-foreground">
              用于生成邀请链接等场景的域名。留空则使用系统默认地址（FRONTEND_URL 环境变量）。
            </p>
          </div>
          <Button
            onClick={handleFrontendUrlSave}
            disabled={setFrontendUrlMutation.isPending}
          >
            {setFrontendUrlMutation.isPending ? "保存中..." : "保存"}
          </Button>
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">登录配置</h2>
          <div className="space-y-2">
            <Label htmlFor="login-default-route">登录后默认页面</Label>
            <Input
              id="login-default-route"
              value={loginDefaultRoute}
              onChange={(e) => setLoginDefaultRoute(e.target.value)}
              placeholder="/workbench"
            />
            <p className="text-xs text-muted-foreground">
              用户没有指定回跳地址时进入的站内路由（默认 /workbench
              工作台），可包含查询参数和锚点，例如 /chat 或
              /data-center?tab=queries。
            </p>
          </div>
          <Button
            onClick={handleLoginDefaultRouteSave}
            disabled={setLoginDefaultRouteMutation.isPending}
          >
            {setLoginDefaultRouteMutation.isPending ? "保存中..." : "保存"}
          </Button>
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">注册配置</h2>
          <div className="flex items-center justify-between space-y-2">
            <div className="space-y-1">
              <Label htmlFor="allow-self-registration">允许用户自助注册</Label>
              <p className="text-xs text-muted-foreground">
                关闭后登录页不再展示注册入口；通过邀请链接加入不受影响。
              </p>
            </div>
            <Switch
              id="allow-self-registration"
              checked={allowRegistration}
              onCheckedChange={handleAllowRegistrationChange}
              disabled={setAllowRegistrationMutation.isPending}
            />
          </div>
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">备案配置</h2>
          <div className="space-y-2">
            <Label htmlFor="icp-beian-no">ICP 备案号</Label>
            <Input
              id="icp-beian-no"
              value={icpBeianNo}
              onChange={(e) => setIcpBeianNo(e.target.value)}
              placeholder="京ICP备XXXXXXXX号"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="police-beian-no">公安备案号</Label>
            <Input
              id="police-beian-no"
              value={policeBeianNo}
              onChange={(e) => setPoliceBeianNo(e.target.value)}
              placeholder="X公网安备XXXXXXXXXXXXX号"
            />
            <p className="text-xs text-muted-foreground">
              配置后登录页/注册页版权行下方展示备案号链接；留空则不展示。
            </p>
          </div>
          <Button
            onClick={handleBeianSave}
            disabled={setBeianMutation.isPending}
          >
            {setBeianMutation.isPending ? "保存中..." : "保存"}
          </Button>
        </div>
        <PythonBinSection />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_app/settings/system")({
  component: SystemConfigPage,
});

/** 智能体工坊分区：代码工具 Python 解释器路径（SystemConfig agent-tool.pythonBin） */
function PythonBinSection() {
  const { data, isLoading } = useSysGetConfig(AGENT_TOOL_PYTHON_BIN_KEY);
  const setMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success("已保存");
      },
      onError: () => {
        toast.error("保存失败");
      },
    },
  });
  const [value, setValue] = useState("");

  useEffect(() => {
    if (data) {
      setValue(String(data.value ?? ""));
    }
  }, [data]);

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <h2 className="font-medium">智能体工坊</h2>
      <div className="space-y-2">
        <Label htmlFor="agent-tool-python-bin">Python 解释器路径</Label>
        <Input
          id="agent-tool-python-bin"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="/usr/bin/python3"
          className="font-mono"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          工坊代码工具（Python）的执行解释器，对 server 试运行与 worker
          执行同时生效。留空使用默认 python3（容器镜像内置
          /usr/bin/python3）。本地开发请使用 .env.primary.local（机器级，优先于本设置）。
        </p>
      </div>
      <Button onClick={() => setMutation.mutate({ key: AGENT_TOOL_PYTHON_BIN_KEY, data: { value: value.trim() } })} disabled={setMutation.isPending}>
        {setMutation.isPending ? "保存中..." : "保存"}
      </Button>
    </div>
  );
}
